import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, MapPin, User, Users, ArrowLeft, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { PT_DIOCESES } from "@/lib/dioceses";

export default function EventDetail() {
  const { t, lang } = useI18n();
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", diocese: "", parish: "", marketing_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const load = () => api.get(`/events/${id}`).then((r) => setE(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const shareEvent = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/og/events/${id}`;
    const shareData = { title: e?.title, text: e?.title, url };
    try {
      if (navigator.share) { await navigator.share(shareData); return; }
      await navigator.clipboard.writeText(url);
      toast.success(t("share_copied"));
    } catch (err) { /* user cancelled */ }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.diocese || !form.parish) { toast.error(t("ev_diocese") + " / " + t("ev_parish")); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/events/${id}/register`, { ...form, origin_url: window.location.origin });
      if (data.paid && data.checkout_url) {
        toast.success(t("ev_redirect"));
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(t("register_success"));
      setDone(true);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (!e) return <div className="max-w-4xl mx-auto px-4 py-20 text-slate-500">{t("loading")}</div>;

  return (
    <div>
      <section className="relative bg-th-blue text-white">
        <div className="absolute inset-0"><img src={e.image_url} alt="" className="w-full h-full object-cover opacity-40" /><div className="absolute inset-0 bg-gradient-to-t from-th-bluedark via-th-blue/80 to-th-blue/60" /></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Link to="/eventos" className="inline-flex items-center gap-1 text-white/70 hover:text-th-gold text-sm mb-6" data-testid="back-events"><ArrowLeft className="w-4 h-4" /> {t("events_title")}</Link>
          <h1 className="font-display font-bold text-4xl sm:text-5xl max-w-3xl">{e.title}</h1>
          <Button type="button" onClick={shareEvent} data-testid="event-share-btn" className="mt-5 bg-th-gold text-th-bluedark hover:bg-th-goldlight font-semibold gap-2"><Share2 className="w-4 h-4" />{t("share")}</Button>
          <div className="mt-5 flex flex-wrap gap-5 text-white/85">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-th-gold" />{new Date(e.date).toLocaleString(lang, { dateStyle: "full", timeStyle: "short" })}{e.end_date ? ` — ${new Date(e.end_date).toLocaleString(lang, { dateStyle: (new Date(e.end_date).toDateString() === new Date(e.date).toDateString() ? undefined : "medium"), timeStyle: "short" })}` : ""}</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-th-gold" />{[e.location, e.address].filter(Boolean).join(" · ")}</span>
            {e.organizer && <span className="flex items-center gap-2"><User className="w-4 h-4 text-th-gold" />{e.organizer}</span>}
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-th-gold" />{e.registrations_count} inscritos</span>
            <span className="flex items-center gap-2 font-semibold text-th-goldlight" data-testid="event-price">{e.price > 0 ? `${e.price.toFixed(2)} €` : t("ev_free")}</span>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-3">
          {e.image_url && <img src={e.image_url} alt={e.title} data-testid="event-image" className="w-full max-h-[420px] object-cover rounded-2xl shadow-lg mb-8" />}
          <div className="text-slate-600 leading-relaxed text-lg whitespace-pre-line" data-testid="event-description">{e.description}</div>
        </div>
        <div className="md:col-span-2">
          <div className="bg-white rounded-2xl p-6 border border-th-gold/20 shadow-lg sticky top-24">
            <h2 className="font-display font-semibold text-xl text-th-blue">{t("event_register")}</h2>
            {e.price > 0 && <div className="mt-1 text-2xl font-display font-bold text-th-gold" data-testid="event-register-price">{e.price.toFixed(2)} €</div>}
            {e.registration_enabled === false ? (
              <div className="mt-4 text-slate-500 bg-slate-50 rounded-lg p-4 text-sm">Inscrições encerradas.</div>
            ) : done ? (
              <div className="mt-4 text-green-700 bg-green-50 rounded-lg p-4 text-sm" data-testid="event-register-success">{t("register_success")}</div>
            ) : (
              <form onSubmit={submit} className="mt-4 space-y-4">
                <div><Label>{t("ev_name")}</Label><Input required value={form.name} data-testid="event-name-input" onChange={(ev) => setForm({ ...form, name: ev.target.value })} className="mt-1" /></div>
                <div><Label>{t("form_email")}</Label><Input required type="email" value={form.email} data-testid="event-email-input" onChange={(ev) => setForm({ ...form, email: ev.target.value })} className="mt-1" /></div>
                <div><Label>{t("ev_diocese")}</Label>
                  <Select value={form.diocese} onValueChange={(v) => setForm({ ...form, diocese: v })}>
                    <SelectTrigger className="mt-1" data-testid="event-diocese-input"><SelectValue placeholder={t("ev_diocese")} /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {PT_DIOCESES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("ev_parish")}</Label><Input required value={form.parish} data-testid="event-parish-input" onChange={(ev) => setForm({ ...form, parish: ev.target.value })} className="mt-1" /></div>
                <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-600">
                  <Checkbox checked={form.marketing_consent} data-testid="event-consent" onCheckedChange={(v) => setForm({ ...form, marketing_consent: !!v })} className="mt-0.5" />
                  <span>{t("ev_consent")}</span>
                </label>
                <Button type="submit" disabled={submitting} data-testid="event-register-submit" className="w-full bg-th-blue text-white hover:bg-th-bluelight">{submitting ? t("submitting") : (e.price > 0 ? t("ev_pay") : t("event_register"))}</Button>
                {e.price > 0 && <p className="text-xs text-slate-400 text-center">Stripe · Modo de teste · 4242 4242 4242 4242</p>}
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
